import React, { useEffect, useId, useMemo, useState } from 'react';
import { Copy, Eye, Loader2, Save } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge, EmptyState, ErrorState, IconButton, LoadingState, SeverityBadge, Tab, TabList, TabPanel, Tabs, useConfirm } from '@/components/primitives';
import { adminApi, cleanIocValue, copyText, formatUtc, iocTypeLabel, plural, toastError, useAdminQuery, useBusy } from './adminApi';

const parseLines = (text) => Array.from(new Set(text.split(/\r?\n/).map(cleanIocValue).filter(Boolean)));

function RuleBlock({ rule }) {
  return (
    <div className="adm-rule">
      <div className="adm-rule-head">
        <span className="adm-mono adm-break">{rule.rule_name}</span>
        <IconButton label={`Copy ${rule.rule_name}`} icon={Copy} size="sm" onClick={() => copyText(rule.rule_content, 'Rule')} />
      </div>
      <pre className="adm-code">{rule.rule_content}</pre>
    </div>
  );
}

function RuleList({ yara, sigma, emptyText }) {
  const [tab, setTab] = useState('yara');
  if (!yara.length && !sigma.length) return <EmptyState variant="compact" title={emptyText} />;
  return (
    <Tabs value={tab} onValueChange={setTab}>
      <TabList label="Rule format">
        <Tab value="yara" count={yara.length}>YARA</Tab>
        <Tab value="sigma" count={sigma.length}>Sigma</Tab>
      </TabList>
      <TabPanel value="yara" className="adm-rule-panel">
        {yara.length ? yara.map((r) => <RuleBlock key={r.rule_name} rule={r} />) : <EmptyState variant="compact" title="No YARA rule" />}
      </TabPanel>
      <TabPanel value="sigma" className="adm-rule-panel">
        {sigma.length ? sigma.map((r) => <RuleBlock key={r.rule_name} rule={r} />) : <EmptyState variant="compact" title="No Sigma rules" />}
      </TabPanel>
    </Tabs>
  );
}

/**
 * Rule editor for one brief. The admin curates the indicator list (one per
 * line); the server decides which values a rule can match (public IPs,
 * domains, URLs, hashes) and builds YARA + Sigma from them. Preview never
 * stores anything; Save replaces the brief's stored rules after confirmation.
 */
export default function RuleEditorDialog({ attackId, onOpenChange, onSaved }) {
  const open = Boolean(attackId);
  const query = useAdminQuery(
    () => adminApi.get(`/admin/attacks/${attackId}/rule-set`).then((r) => r.data),
    [attackId],
    { enabled: open }
  );
  const [text, setText] = useState('');
  const [preview, setPreview] = useState(null); // { forText, indicators, yara, sigma }
  const [view, setView] = useState('preview');
  const { run, busy } = useBusy();
  const [confirm, confirmDialog] = useConfirm();
  const textId = useId();

  const data = query.data;
  useEffect(() => {
    if (!data) return;
    const initial = data.indicators.map((i) => i.value).join('\n');
    setText(initial);
    setPreview(null);
    setView(data.yara.length || data.sigma.length ? 'stored' : 'preview');
  }, [data]);

  const values = useMemo(() => parseLines(text), [text]);
  const stale = preview && preview.forText !== text;

  const doPreview = () => run('preview', async () => {
    try {
      const res = await adminApi.post(`/admin/attacks/${attackId}/rule-set/preview`, { iocs: values });
      setPreview({ forText: text, ...res.data });
      setView('preview');
    } catch (err) {
      toastError(err, 'Could not build a preview');
    }
  });

  // Build a first preview as soon as the indicators are loaded.
  useEffect(() => {
    if (data && !preview && values.length && data.attack?.content_type !== 'informational') doPreview();
  }, [data, values.length]);

  const typed = preview && !stale ? preview.indicators : null;
  const usable = typed ? typed.filter((i) => i.type) : [];
  const skipped = typed ? typed.filter((i) => !i.type) : [];
  const byType = usable.reduce((acc, i) => ({ ...acc, [i.type]: (acc[i.type] || 0) + 1 }), {});

  const save = async () => {
    const stored = (data?.yara.length || 0) + (data?.sigma.length || 0);
    const ok = await confirm({
      title: `Save rules for "${data?.attack?.name}"?`,
      description: stored
        ? `The ${plural(stored, 'stored rule')} for this brief are replaced. Customers matched to it download the new rules.`
        : 'Rules are created for this brief. Customers matched to it can download them.',
      confirmLabel: 'Save rules',
    });
    if (!ok) return;
    run('save', async () => {
      try {
        const res = await adminApi.put(`/admin/attacks/${attackId}/rule-set`, { iocs: values });
        const r = res.data;
        toast.success('Rules saved', {
          description: `${plural(r.yara.length, 'YARA rule')}, ${plural(r.sigma.length, 'Sigma rule')}${r.skipped?.length ? `; ${plural(r.skipped.length, 'value')} skipped` : ''}.`,
        });
        onSaved?.();
        onOpenChange(false);
      } catch (err) {
        toastError(err, 'Could not save the rules');
      }
    });
  };

  const informational = data?.attack?.content_type === 'informational';

  return (
    <Dialog open={open} onOpenChange={(v) => !busy('save') && onOpenChange(v)}>
      <DialogContent className="adm-dialog adm-dialog--editor" data-testid="rules-edit-dialog">
        <DialogHeader className="adm-editor-head">
          <DialogTitle className="adm-break">Detection rules: {data?.attack?.name || 'Loading…'}</DialogTitle>
          <DialogDescription>
            One indicator per line. Values a rule cannot match (names, phrases, private IPs) are skipped automatically.
          </DialogDescription>
          {data && (
            <div className="adm-detail-meta">
              <SeverityBadge severity={data.attack.severity} size="sm" />
              {data.curated && <Badge plain size="sm">Curated list</Badge>}
              <span className="adm-meta">Rules updated {data.rules_updated_at ? formatUtc(data.rules_updated_at) : 'never by an admin'}</span>
            </div>
          )}
        </DialogHeader>

        <div className="adm-editor-body">
          {query.loading ? (
            <LoadingState label="rules" rows={5} />
          ) : query.error ? (
            <ErrorState title="Couldn't load this brief's rules" error={query.error} onRetry={query.reload} />
          ) : informational ? (
            <EmptyState variant="compact" title="Informational brief" description="Informational briefs (advisories, news) do not get detection rules." />
          ) : (
            <div className="adm-editor-grid">
              <div className="adm-field">
                <label htmlFor={textId} className="adm-label">Indicators ({values.length})</label>
                <textarea
                  id={textId}
                  className="adm-input adm-mono adm-editor-text"
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  spellCheck={false}
                  rows={14}
                  data-testid="rule-indicators"
                />
                {typed ? (
                  <div className="adm-editor-summary" aria-live="polite">
                    <span>
                      {plural(usable.length, 'usable indicator')}
                      {usable.length ? `: ${Object.entries(byType).map(([t, n]) => `${n} ${iocTypeLabel(t)}`).join(', ')}` : ''}
                    </span>
                    {skipped.length > 0 && (
                      <details>
                        <summary>{plural(skipped.length, 'value')} will be skipped</summary>
                        <ul className="adm-skipped">
                          {skipped.map((i) => <li key={i.value}><code>{i.value}</code></li>)}
                        </ul>
                      </details>
                    )}
                  </div>
                ) : (
                  <p className="adm-hint">{stale ? 'The list changed: preview again to see which values are usable.' : 'Preview to see which values a rule can use.'}</p>
                )}
              </div>
              <div className="adm-editor-rules">
                <div className="adm-chips" role="group" aria-label="Rules shown">
                  <button type="button" className="adm-chip" aria-pressed={view === 'preview'} onClick={() => setView('preview')}>Preview</button>
                  <button type="button" className="adm-chip" aria-pressed={view === 'stored'} onClick={() => setView('stored')}>
                    Stored now <span className="adm-chip-count">{(data?.yara.length || 0) + (data?.sigma.length || 0)}</span>
                  </button>
                </div>
                {view === 'stored' ? (
                  <RuleList yara={data?.yara || []} sigma={data?.sigma || []} emptyText="No rules are stored for this brief" />
                ) : busy('preview') && !preview ? (
                  <LoadingState label="preview" variant="spinner" />
                ) : preview ? (
                  <>
                    {stale && <p className="adm-note adm-note--warning" role="note">Preview is out of date with the indicator list.</p>}
                    <RuleList yara={preview.yara} sigma={preview.sigma} emptyText="These indicators produce no rule" />
                  </>
                ) : (
                  <EmptyState variant="compact" title="No preview yet" description="Add indicators, then preview the generated rules." />
                )}
              </div>
            </div>
          )}
        </div>

        <div className="adm-editor-foot">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy('save')}>Cancel</Button>
          <Button variant="outline" onClick={doPreview} disabled={!data || informational || busy('preview') || !values.length}>
            {busy('preview') ? <Loader2 className="is-spinner" aria-hidden="true" /> : <Eye aria-hidden="true" />}
            Preview
          </Button>
          <Button onClick={save} disabled={!data || informational || busy('save') || !values.length || (typed && !usable.length)} data-testid="update-rules-btn">
            {busy('save') ? <Loader2 className="is-spinner" aria-hidden="true" /> : <Save aria-hidden="true" />}
            {busy('save') ? 'Saving…' : 'Save rules'}
          </Button>
        </div>
        {confirmDialog}
      </DialogContent>
    </Dialog>
  );
}
